/**
 * ScrollLockManager
 *
 * Manages body scroll lock state using reference counting.
 * Multiple popups can acquire/release scroll lock independently,
 * and the body class is only removed when all have released.
 *
 * This solves the coordination problem when multiple popups are open:
 * - Standard and fullscreen popups need scroll lock
 * - Corner popups need scroll lock only when backdrop is shown
 * - Banner popups do NOT need scroll lock
 *
 * Without this manager, closing one popup might incorrectly remove
 * the scroll lock even when another popup still needs it.
 */

const SCROLL_LOCK_CLASS = 'gp-popup-open';

class ScrollLockManagerClass {
	private count = 0;

	/**
	 * Acquire a scroll lock. Adds the body class if this is the first lock.
	 */
	acquire(): void {
		this.count++;
		if (this.count === 1) {
			document.body.classList.add(SCROLL_LOCK_CLASS);
		}
	}

	/**
	 * Release a scroll lock. Removes the body class if this was the last lock.
	 */
	release(): void {
		this.count = Math.max(0, this.count - 1);
		if (this.count === 0) {
			document.body.classList.remove(SCROLL_LOCK_CLASS);
		}
	}

	/**
	 * Get the current lock count (for debugging).
	 */
	getCount(): number {
		return this.count;
	}

	/**
	 * Force reset the lock state (for testing/cleanup).
	 */
	reset(): void {
		this.count = 0;
		document.body.classList.remove(SCROLL_LOCK_CLASS);
	}
}

// Export singleton instance
export const ScrollLockManager = new ScrollLockManagerClass();
